/**
 * Ayrshire Hub — shared design tokens (web).
 *
 * Centralises the values that were previously copy-pasted across pages:
 * chip colour tones, category colours/emoji, and urgency config.
 *
 * Palette is intentionally the existing Ayrshire Hub one — navy #1e3a5f
 * primary, gold #f0a500 accent. This file does not introduce new brand
 * colours, it just stops each page inventing its own Tailwind strings.
 */

/* ── Chip / badge tones ───────────────────────────────────────────────
   One entry per semantic tone. Pages pick a tone, never raw classes.  */
export type Tone =
  | "neutral" | "primary" | "gold" | "success" | "warning"
  | "danger"  | "info"    | "violet" | "orange";

export const TONE_SOLID: Record<Tone, string> = {
  neutral: "bg-slate-100 text-slate-700",
  primary: "bg-[#e8eef5] text-[#1e3a5f]",
  gold:    "bg-amber-50 text-amber-700",
  success: "bg-emerald-50 text-emerald-700",
  warning: "bg-amber-50 text-amber-700",
  danger:  "bg-red-50 text-red-700",
  info:    "bg-blue-50 text-blue-700",
  violet:  "bg-violet-50 text-violet-700",
  orange:  "bg-orange-50 text-orange-600",
};

export const TONE_OUTLINE: Record<Tone, string> = {
  neutral: "border-slate-200 text-slate-600",
  primary: "border-[#c7d6e6] text-[#1e3a5f]",
  gold:    "border-amber-200 text-amber-700",
  success: "border-emerald-200 text-emerald-700",
  warning: "border-amber-200 text-amber-700",
  danger:  "border-red-200 text-red-700",
  info:    "border-blue-200 text-blue-700",
  violet:  "border-violet-200 text-violet-700",
  orange:  "border-orange-200 text-orange-600",
};

/** Dark, high-contrast fill — for overlays on imagery. */
export const TONE_OVERLAY = "bg-black/60 text-white backdrop-blur-sm";

/* ── Category colours ─────────────────────────────────────────────────
   Previously duplicated as CATEGORY_COLOURS in Events/Deals/News.      */
export const CATEGORY_TONE: Record<string, Tone> = {
  // Food & drink
  "Takeaway": "orange", "Restaurant": "orange", "Cafe": "orange", "Café": "orange",
  "Pizza": "orange", "Indian": "orange", "Chinese": "orange", "Chippy": "orange",
  // Retail
  "Grocery": "success", "Convenience": "success", "Butcher": "success",
  "Bakery": "success", "Off Licence": "success", "Retail": "success",
  // Events
  "Live Music": "violet", "Karaoke": "violet", "Quiz Night": "violet",
  "Darts Night": "violet", "Pool Night": "violet", "Nightlife": "violet",
  "Family": "info", "Charity": "danger", "Sports": "info", "Market": "gold",
  // Trades
  "Plumbing": "info", "Electrical": "gold", "Joinery": "orange",
  "Painting": "violet", "Gardening": "success", "Roofing": "neutral",
};

export function categoryTone(category?: string | null): Tone {
  if (!category) return "neutral";
  return CATEGORY_TONE[category] ?? "neutral";
}

/* ── Urgency (quote requests) ─────────────────────────────────────────
   Previously URGENCY_CONFIG duplicated in Jobs.tsx and Home.tsx.       */
export const URGENCY: Record<string, { tone: Tone; dot: string; label: string }> = {
  emergency: { tone: "danger",  dot: "bg-red-500",     label: "Emergency" },
  urgent:    { tone: "orange",  dot: "bg-orange-500",  label: "Urgent" },
  soon:      { tone: "warning", dot: "bg-amber-500",   label: "Soon" },
  flexible:  { tone: "success", dot: "bg-emerald-500", label: "Flexible" },
};

/* ── Layout ───────────────────────────────────────────────────────────
   Standard content width + gutter, so pages stop inventing max-w-*.    */
export const CONTENT_MAX = "max-w-screen-xl mx-auto px-4";

/** Carousel card widths — keeps every horizontal rail visually aligned. */
export const RAIL_CARD = {
  store: "w-[220px]",
  media: "w-[260px]",
  compact: "w-[168px]",
} as const;

/* ── App-aligned scales ───────────────────────────────────────────────
   These mirror mobile `constants/theme.ts` so the same product reads the
   same on web. Native uses numbers; web expresses them as Tailwind
   classes. The VALUES are identical — only the syntax differs.

     radii.md   = 14px  → rounded-[14px]
     type.title2 = 18/24 bold  → text-[18px] leading-6 font-bold
     space.md   = 16px  → gap-4 / p-4

   Rule (same as the app): a page never invents a font size, radius or
   shadow. It picks a token from here.                                  */

/** Border radii — mirrors theme.ts `radii`. */
export const RADII = {
  xs:   "rounded-[6px]",
  sm:   "rounded-[10px]",
  md:   "rounded-[14px]",
  lg:   "rounded-[18px]",
  xl:   "rounded-[24px]",
  /** The canonical card silhouette. theme.ts radii.card = 14. */
  card: "rounded-[14px]",
  pill: "rounded-full",
} as const;

/**
 * Typography — mirrors theme.ts `type`.
 *
 * Native loads Inter_* families per weight; on web one variable family
 * carries all weights, so weight is a class rather than a family name.
 * Sizes and line heights are copied verbatim from theme.ts.
 */
export const TYPE = {
  display:    "text-[28px] leading-[34px] font-bold tracking-[-0.8px]",
  title1:     "text-[23px] leading-[29px] font-bold tracking-[-0.6px]",
  title2:     "text-[18px] leading-[24px] font-bold tracking-[-0.3px]",
  title3:     "text-[16px] leading-[21px] font-bold tracking-[-0.2px]",
  /** Card titles. */
  headline:   "text-[15px] leading-[20px] font-bold tracking-[-0.2px]",
  body:       "text-[15px] leading-[21px] font-normal",
  bodyStrong: "text-[15px] leading-[21px] font-semibold",
  callout:    "text-[14px] leading-[19px] font-normal",
  /** Card metadata ("Pizza · Kilmarnock"). */
  subhead:    "text-[13px] leading-[18px] font-normal",
  footnote:   "text-[12px] leading-[16px] font-normal",
  /** Chips and badges. */
  caption:    "text-[11px] leading-[14px] font-semibold tracking-[0.1px]",
  /** Uppercase micro-labels. Use sparingly. */
  overline:   "text-[10px] leading-[13px] font-bold tracking-[0.8px] uppercase",
} as const;

/** Shadows — mirrors theme.ts `elevation` (web branch of the Platform.select). */
export const ELEVATION = {
  none: "",
  sm:   "shadow-[0_1px_3px_rgba(15,23,42,0.06)]",
  md:   "shadow-[0_2px_10px_rgba(15,23,42,0.08)]",
  lg:   "shadow-[0_4px_16px_rgba(15,23,42,0.12)]",
  xl:   "shadow-[0_8px_28px_rgba(15,23,42,0.18)]",
} as const;

/**
 * Focus ring for keyboard users. Applied to every interactive card and
 * control so tab-navigation is visible without adding a hover-only affordance.
 */
export const FOCUS_RING =
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#1e3a5f] focus-visible:ring-offset-2";

/**
 * Minimum touch target. 44px is the Apple HIG floor and matches
 * theme.ts MIN_TAP_TARGET. Applied to controls that are small visually
 * but must stay tappable on a phone.
 */
export const TAP_TARGET = "min-h-11 min-w-11";

/**
 * Browse grid.
 *
 * Desktop earns its width by showing MORE CARDS SIDE BY SIDE, not larger
 * ones. Column counts are the brief's: ~4 at >=1440, 3 at 1280, 2 at 768,
 * 1 on phone. Card height stays constant across all of them.
 */
/*
 * All four column steps use arbitrary `min-[…]` variants rather than a mix of
 * named breakpoints and arbitrary ones. Tailwind emits named and arbitrary
 * variants in separate blocks, so `xl:grid-cols-3` was winning over
 * `min-[1440px]:grid-cols-4` on source order and 1440/1920 both rendered three
 * columns. Keeping them all arbitrary makes them sort by width, as intended.
 */
export const BROWSE_GRID =
  "grid grid-cols-1 min-[700px]:grid-cols-2 min-[1000px]:grid-cols-3 min-[1440px]:grid-cols-4 gap-3 sm:gap-4";
